import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface YouchuanRetextureParams {
  /**
   * 图像URL，必须是有效的HTTP/HTTPS URL，长度不超过1024字符
   */
  imgUrl: string;
  /**
   * 重混提示词，长度必须在1-8192字符之间
   */
  remixPrompt: string;
  /**
   * 回调URL，必须是有效的HTTP/HTTPS URL
   */
  callback?: string;
}

export interface YouchuanRetextureResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * youchuan_retexture
 *
 * Youchuan Retexture Image Generator
 *
 * Image retexturing implementation for Youchuan Open Platform (悠船开放平台).
 *
 * API Documentation: https://tob.youchuan.cn/openapi/apis/%E5%9B%BE%E7%89%87%E7%94%9F%E6%88%90/api_retexture
 *
 * @example
 * ```typescript
 * import { initSeacloud, youchuanRetexture } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await youchuanRetexture({
 *   imgUrl: 'https://example.com/image.jpg',
 *   remixPrompt: 'Change texture to wood'
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 YouchuanRetextureParams 接口定义
 * @returns 生成的资源列表
 */
export async function youchuanRetexture(
  params: YouchuanRetextureParams,
): Promise<YouchuanRetextureResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'youchuan_retexture',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: YouchuanRetextureResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
